<template>
  <main>
    <Dashboard :title="$t('restrat.blast.page_title')" iconsOnly="true" noPadding="true">
      <div v-if="loading" class="flex justify-center py-10">
        <svg class="w-16 h-16 text-white animate-spin" fill="none" viewBox="0 0 24 24">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            fill="currentColor"
          />
        </svg>
      </div>
      <div v-else class="flex flex-1 min-h-screen">
        <div class="flex flex-col xl:flex-row flex-1">
          <div class="flex flex-col flex-1">
            <!-- video display -->
            <div class="w-full aspect-[16/9] relative" v-if="!videoLoading">
              <iframe
                class="w-full aspect-[16/9] absolute top-0 left-0"
                :src="activeRoutine?.video_url"
                :title="$t('restrat.blast.video_player')"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              />
            </div>
            <div v-else class="flex w-full aspect-[16/9] items-center justify-center">
              <svg
                class="w-16 h-16 text-white animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  fill="currentColor"
                />
              </svg>
            </div>

            <div class="flex-1 w-full bg-gray-950 px-9 py-6">
              <h1 class="text-text-primary font-semibold flex items-center text-xl">
                {{ activeRoutine?.name || $t('restrat.blast.select_highlight') }}
              </h1>
              <h2 class="text-text-secondary text-lg pt-3">
                {{ activeRoutine?.description || blastTaskList?.description || $t('restrat.blast.default_description') }}
              </h2>
            </div>
          </div>

          <!-- right nav -->
          <div class="flex flex-col bg-gray-900 w-[420px] p-6 lg:sticky lg:top-0 lg:self-start lg:overflow-y-auto" style="height: calc(100vh - 64px);">
            <div class="w-full gap-4 flex flex-col bg-gray-900 flex-shrink-0">
              <h1 class="text-text-primary text-xl font-bold pb-4">{{ $t("restrat.blast.playlist_title") }}</h1>
            </div>

            <!-- routines playlist -->
            <div class="select-none flex flex-col gap-2 flex-1 bg-gray-900">
              <div
                v-for="(routine, index) in customRoutines"
                :key="routine.id"
                class="items-center cursor-pointer rounded-lg overflow-hidden"
              >
                <div
                  @click="updateActiveRoutine(routine)"
                  class="p-4 flex"
                  :class="[
                    routine.id === activeRoutine?.id
                      ? 'bg-primary-600 rounded-t-lg text-text-primary'
                      : 'text-text-secondary bg-gray-800 rounded-lg',
                  ]"
                >
                  <h1 class="uppercase text-[14px] tracking-wide font-semibold flex w-full items-center">
                    {{ $t("restrat.blast.day_label", { day: index + 1, name: routine.name }) }}
                  </h1>
                  <PlayIcon class="h-5 w-5 flex" aria-hidden="true" />
                </div>

                <div
                  v-if="routine.id === activeRoutine?.id"
                  class="bg-gray-800 rounded-b-lg p-4"
                >
                  <button
                    @click.stop="launchRoutine(routine)"
                    :disabled="launchLoading"
                    class="w-full py-2 rounded-lg text-text-primary text-sm font-medium duration-300 flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 disabled:bg-gray-800 disabled:cursor-not-allowed"
                  >
                    <svg
                      v-if="launchLoading"
                      width="16"
                      height="16"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="animate-spin"
                    >
                      <circle cx="16" cy="16" r="15" stroke="#1E2733" stroke-width="2" />
                      <path
                        d="M16 1C17.9698 1 19.9204 1.38799 21.7403 2.14181C23.5601 2.89563 25.2137 4.00052 26.6066 5.3934C27.9995 6.78628 29.1044 8.43986 29.8582 10.2597C30.612 12.0796 31 14.0302 31 16"
                        stroke="#4E54C8"
                        stroke-width="2"
                      />
                    </svg>
                    <HandThumbUpIcon
                      v-if="launchSuccess"
                      class="w-4 h-4 text-success-400"
                    />
                    <span v-else>{{ $t("restrat.blast.launch_scenario") }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
    <StartServerModal :server="serverInfo" :show="showServerModal" @close="showServerModal = false" />
  </main>
</template>

<script setup>
import { ref, computed } from 'vue';
import { PlayIcon, HandThumbUpIcon } from '@heroicons/vue/20/solid';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import { useStore } from 'vuex';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import StartServerModal from '../components/StartServerModal.vue';
import Dashboard from '../components/layouts/DashboardLayout.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const activeRoutine = ref(null);
const customRoutines = ref([]);
const blastTaskList = ref(null);
const loading = ref(true);
const videoLoading = ref(false);
const launchLoading = ref(false);
const launchSuccess = ref(false);
const showServerModal = ref(false);
const serverInfo = ref();
const activeServer = computed(() => store.getters.activeServer);

useHead({
  title: () => (activeRoutine.value?.name ? t('restrat.blast.head_title_routine', { name: activeRoutine.value.name }) : t('restrat.blast.head_title')),
});

function updateActiveRoutine(routine) {
  if (!routine) return;

  videoLoading.value = true;
  activeRoutine.value = routine;

  // Simulate video loading delay
  setTimeout(() => {
    videoLoading.value = false;
  }, 300);
}

function launchRoutine(section) {
  if (!section || !blastTaskList.value) return;

  // Launch the first task in the section
  const taskListId = blastTaskList.value.id;
  const firstTask = section.tasks?.[0];

  // Always show modal to allow difficulty selection (whether server exists or not)
  serverInfo.value = {
    mode: 'Creator Studio',
    content: {
      task_list_id: taskListId,
      task_list_section_id: section.id,
      id: firstTask?.id || 0, // Task ID for launch command
      task_list_type: 'playlist',
      is_exam: false,
      title: section.name,
      type: 'task',
      use_difficulty: 1, // Enable difficulty selector for BLAST tasks
    },
  };
  showServerModal.value = true;
}

function getCustomRoutines() {
  loading.value = true;
  axios
    .get('/task_lists/blast_premier')
    .then((response) => {
      blastTaskList.value = response.data;
      // Extract sections as routines
      customRoutines.value = response.data.sections || [];
      // Set first section as active by default
      if (customRoutines.value.length > 0) {
        updateActiveRoutine(customRoutines.value[0]);
      }
      loading.value = false;
    })
    .catch((error) => {
      console.error(error);
      loading.value = false;
    });
}

getCustomRoutines();
</script>
